import React, { useMemo, useState } from 'react';
import Flex from '../../../components/Flex';
import { Typography } from 'antd';
import { Select } from '@prio365/prio365-react-library';
import { makePrioStyles } from '../../../theme/utils';
import { useTranslation } from 'react-i18next';
import { EmailString, ProjectId } from '../../../models/Types';
import {
  EmailSearchResult,
  EmailSearchResultContact,
  EmailSearchResultProject,
} from '../../../models/Message';
import useAddressSearch from '../hooks/useAddressSearch';
import classNames from 'classnames';
import { emailPattern } from '../../../hooks/useEmailValidation';
import { Classes } from 'jss';
import { debounceFunction } from '../../../util';

const useStylesAddressSelect = makePrioStyles((theme) => ({
  root: {
    flex: 1,
    overflow: 'hidden',
    '& .ant-select-selection-item-content .secondary': {
      display: 'none',
    },
  },
  primary: {
    color: theme.colors.application.typography.default,
    fontSize: theme.font.fontSize.small,
  },
  secondary: {
    marginLeft: theme.spacing.small,
    fontSize: theme.font.fontSize.extraSmall,
    color: theme.colors.application.typography.muted,
  },
  suggestionLabel: {
    fontSize: theme.old.typography.fontSize.tiny,
    color: theme.old.typography.colors.muted,
    padding: `${theme.old.spacing.unit(0.5)}px ${theme.old.spacing.unit(
      1.5
    )}px`,
    borderBottom: theme.old.borders.content,
  },
  label: {
    display: 'flex !important',
    flexDirection: 'row !important',
    alignItems: 'flex-end !important',
    gap: theme.spacing.small,
  },
}));

const debounceSetSearchTerm = debounceFunction(
  (value: string, setSearchTerm: (value: string) => void) => {
    setSearchTerm(value);
  },
  350
);

const emailSearchResultToHTMLText = (
  value: EmailSearchResult,
  classes: Classes
) => {
  if (value.type === 'contact') {
    const contact = value as EmailSearchResultContact;
    if (contact?.lastName && contact?.firstName) {
      return (
        <div className={classes.label}>
          <span className={classes.primary}>
            {`${contact?.firstName} ${contact?.lastName}`}
          </span>
          <span
            className={classNames(classes.secondary)}
          >{`<${contact.eMail}>`}</span>
        </div>
      );
    }
  } else if (value.type === 'project') {
    const project = value as EmailSearchResultProject;
    if (project?.projectName && project?.projectNumber) {
      return (
        <div className={classes.label}>
          <div
            className={classes.primary}
          >{`${project?.projectNumber} ${project?.projectName}`}</div>
          <div
            className={classNames(classes.secondary)}
          >{`<${project.eMail}>`}</div>
        </div>
      );
    }
  }
  return (
    <div className={classNames(classes.primary)}>{`<${value.eMail}>`}</div>
  );
};

interface AddressSelectProps {
  className?: string;
  value?: string[];
  changeHandler?: (value: EmailString[]) => void;
  focus?: boolean;
  bordered?: boolean;
  maxCount?: number | 'responsive';
  open?: boolean;
  closeableTags?: boolean;
  projectId: ProjectId;
  excludedMails?: EmailString[];
}

const AddressSelect: React.FC<AddressSelectProps> = React.memo((props) => {
  //#region ------------------------------ Defaults
  const { className, value, changeHandler, focus, maxCount, open, projectId } =
    props;
  const excludedMails = useMemo(
    () => props.excludedMails ?? [],
    [props.excludedMails]
  );
  const classes = useStylesAddressSelect(props);
  const { t } = useTranslation();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const [searchTerm, setSearchTerm] = useState<string>('');
  const isSearching = searchTerm.length > 2;

  const { addresses } = useAddressSearch(searchTerm, projectId, value);

  const options = useMemo(() => {
    return addresses
      .filter(
        (address) =>
          !!address && !excludedMails?.find((e) => e === address.eMail)
      )
      .map((address) => ({
        id: address.eMail,
        value: address.eMail,
        data: address,
        label: emailSearchResultToHTMLText(address, classes),
      }));
  }, [addresses, excludedMails, classes]);
  //#endregion

  //#region ------------------------------ Effects
  //#endregion

  return (
    <Select<EmailString[]>
      mode="tags"
      className={classNames(classes.root, className)}
      value={value ?? []}
      visible={open}
      onChange={(value) => {
        const emailValues = value.filter((address) =>
          address.match(emailPattern)
        );
        if (changeHandler) {
          changeHandler(emailValues);
        }
      }}
      autoFocus={focus}
      onSearch={(value: string) => {
        debounceSetSearchTerm(value, setSearchTerm);
      }}
      dropdownRender={(menu) => (
        <Flex.Column>
          {!isSearching && projectId !== 'favorites' && (
            <Typography.Text className={classes.suggestionLabel}>
              {t('contacts:actions.suggestions')}
            </Typography.Text>
          )}
          {menu}
        </Flex.Column>
      )}
      filterOption={() => {
        return true;
      }}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
      maxTagCount={maxCount}
      options={options}
    />
  );
});

export default AddressSelect;
